import { Box, IconButton, Stack, Typography, alpha, keyframes, useTheme } from '@mui/material'
import { ScanEyeIcon, XIcon } from 'lucide-react'

import { Tooltip } from '@repo/ui'

import {
  APP_SCREEN_COUNT,
  PREVIEWABLE_FLOW_COUNT,
  TOKEN_IMPACT,
  type StudioTokenName,
} from './tokenImpact'
import { APP_FLOW_PAGES } from './previewConfig'

/** One measured part of the highlight, as reported by whichever preview is live. */
export interface HighlightPartCount {
  label: string
  count: number
}

interface Props {
  token: StudioTokenName
  /** True when THIS control's impact is the one being shown. At most one at a time. */
  active: boolean
  /** Which preview is on screen — decides which half carries the live count. */
  surface: 'certificate' | 'email'
  /** What the live preview actually matched. Only meaningful while `active`. */
  parts?: HighlightPartCount[]
  /**
   * Elements the preview proved this token reaches but could not outline, because they
   * paint nothing you can currently see — almost always the contents of a drawer or sheet
   * that is closed.
   *
   * Worth a sentence rather than a silent zero. "Nothing on this screen" and "nothing you
   * can see from here" are different claims, and where only the second is true this is what
   * says so.
   *
   * The case that produced this branch was `border_radius`: measured on the certificate
   * flow, ALL 43 elements it reached sat inside closed drawers, so an operator who opened
   * that control read "nothing here" about the token that in fact rounds every corner in
   * the app. **That is no longer true** — VRG-562 wired the token to the cards, rows and
   * tiles the previewed flows actually show (21 visible movers on Certificates, the action
   * tiles plus every state card on Certificate), so `border_radius` now reports a real
   * count and never reaches this branch. The branch stays because the CONDITION is general:
   * any token can have its whole reach hidden behind a closed sheet on some flow.
   */
  invisible?: number
  /** Human name of the flow / template currently rendered, for "…on Certificate". */
  previewLabel?: string
}

/** The `id` the toggle's `aria-controls` and the panel's `id` must agree on. */
const panelIdFor = (token: StudioTokenName) => `token-impact-${token}`

/**
 * The affordance: an icon button on the control's title row, labelled by a tooltip.
 *
 * It is still a CLICK target, not a hover one. That part was Spencer's own correction to the
 * original hover-the-preview idea and it still holds: hover is undiscoverable, it conflates
 * *editing* a control with *asking what it touches* (so reaching for a colour picker would
 * set the preview flickering), and it works on neither touch nor a keyboard. Hover here only
 * ever reveals the LABEL. The panel opens on click.
 *
 * ## Why it shrank from a labelled button to a glyph
 *
 * It was `<Button variant="tertiary">What does this change?</Button>`, which is the right
 * component and was the wrong SIZE for how many of them there are: seven controls means seven
 * 40px buttons carrying the same eight-word sentence down one form, and Spencer's read was
 * that they "pollute the page". They did. Repeating a label seven times is not discoverability,
 * it is noise that the eye learns to skip — so the label moves into a tooltip and the glyph
 * stays put, in the same slot, at the same moment of use.
 *
 * ## Why the glyph is `ScanEye` and NOT `(i)`
 *
 * The `(i)` was the obvious candidate and is the one thing here that would have been wrong.
 * In this product that shape is already a promise with a specific meaning — see `InfoGlyph`
 * in `components/feedback/InfoTooltip`, which spells it out: an `i` "explains a TERM or a
 * FIGURE, in place, on hover", it is the glossary affordance of §44, it renders at 41 sites,
 * and the customer guides tell readers in prose to "hover the (i)". THIS control does
 * something else entirely — it opens a persistent panel and repaints the live preview. Same
 * shape, different promise, and the collision would land on this very page, which already
 * renders `<InfoTooltip term="studio" />` next to its own title.
 *
 * `ScanEye` also is not a new invention: it was already this control's `startIcon`. Only the
 * words went away.
 *
 * ## Mechanics
 *
 * `IconButton`, which §60 exempts from the never-`size="small"` rule precisely so an icon
 * affordance can be smaller than a 40px button. `aria-label` carries the same sentence as the
 * tooltip, so the control is still named for a screen reader once the visible text is gone —
 * that is the one real cost of this change and it is paid here.
 */
export const TokenImpactToggle = ({
  token,
  active,
  onToggle,
}: {
  token: StudioTokenName
  active: boolean
  onToggle: (token: StudioTokenName) => void
}) => {
  const label = active ? 'Hide what this changes' : 'What does this change?'

  return (
    <Tooltip title={label}>
      <IconButton
        size="small"
        onClick={() => onToggle(token)}
        aria-label={label}
        aria-expanded={active}
        aria-controls={panelIdFor(token)}
        sx={{ color: active ? 'text.primary' : 'text.secondary' }}
      >
        {active ? <XIcon size={16} strokeWidth={1} /> : <ScanEyeIcon size={16} strokeWidth={1} />}
      </IconButton>
    </Tooltip>
  )
}

/** "a, b and c" — the panel names surfaces in prose, not as a bulleted stack. */
const list = (items: string[]): string =>
  items.length <= 1
    ? (items[0] ?? '')
    : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`

const flowLabel = (value: string): string =>
  (APP_FLOW_PAGES.find(f => f.value === value)?.label as string) ?? value

/**
 * EXPAND, on every open — the containing card grows into the panel rather than jumping.
 *
 * The previous version faded and slid the panel (`opacity` + `translateY(-6px)`) but animated
 * nothing about its HEIGHT, so the panel mounted at full size and the card around it snapped
 * to its new height in a single frame. The content arrived gently inside a box that had
 * already banged open. Reported as "flash expand", which is exactly what it was.
 *
 * The height is animated with `grid-template-rows: 0fr -> 1fr` on a one-row grid whose child
 * clips. This is the only CSS-only way to animate to a height nobody has measured: `height:
 * auto` is not interpolable, and the alternatives are worse — a `max-height` guess either
 * clips a long panel or spends most of the duration animating through empty space, so the
 * apparent speed changes with the length of the text, and a JS measure-then-set costs a
 * ResizeObserver and a reflow to do worse.
 *
 * `min-height: 0` + `overflow: hidden` on the inner element are both load-bearing. A grid item
 * defaults to `min-height: auto`, which refuses to shrink below its content, and without that
 * override the row is pinned at full height and the animation resolves to nothing at all.
 *
 * Still an `animation` and not a `transition`, for the reason it always was: the panel
 * unmounts when its control closes (`if (!active) return null`), so a fresh mount replays the
 * keyframes every time, whereas a transition would fire once on first paint and then need
 * state to retrigger. It deliberately does not replay when the measured counts land a moment
 * later — that is a prop change on a mounted element, not a remount, so the panel settles once
 * and then fills in.
 *
 * TWO keyframes, not three, and that is a correctness point rather than brevity. This first
 * carried a `40% { opacity: 0 }` stop meant to hold the text invisible until the box had
 * mostly opened. It did the opposite, because `animation-timing-function` applies to EACH
 * SEGMENT between keyframes, not once across the whole run: the ease was replayed over
 * `0->40%` and again over `40->100%`, and measuring the scrubbed curve showed opacity already
 * at 0.75 while the box was only 85% open. A third keyframe here buys a second easing curve
 * nobody asked for. If the fade ever genuinely needs to lag, give opacity its OWN animation
 * with its own delay rather than adding a stop to this one.
 *
 * `prefers-reduced-motion` drops the whole thing to a plain appearance — an expanding box is
 * precisely the kind of motion that setting exists for.
 */
const panelIn = keyframes`
  from { grid-template-rows: 0fr; opacity: 0; }
  to   { grid-template-rows: 1fr; opacity: 1; }
`

/**
 * The expanded panel: what this control changes in the app, and what it changes in an email.
 *
 * ## Two sections, not three
 *
 * It used to be three: what is outlined right now, "Across the app", and the email half. The
 * first two were one thought split in two — the measured count and the statement of what that
 * count is a sample OF only mean anything read together, and separating them let the reader
 * take the count as the whole answer, which is the exact failure this feature exists to
 * prevent. They are now one "On app preview" section, count first and scope immediately under
 * it.
 *
 * ## Why the off-screen half is mandatory rather than a nice-to-have
 *
 * The preview renders four of the app's screens. A highlight alone would therefore show a
 * fraction of a token's reach while *feeling* like the whole of it — worse than showing
 * nothing, because the operator would leave believing they had seen the blast radius. So the
 * two halves are not independent: the measured count is only honest BECAUSE the sentence
 * under it names what is off-screen. Do not ship one without the other.
 *
 * ## Why the count is measured and the surfaces are stated
 *
 * The count comes back from the live preview (`tokenHighlight.ts` in `apps/app`, or the
 * email document here), so it describes the DOM in front of you. The surface list is static
 * (`tokenImpact.ts`), because those screens are not rendered and cannot be counted. Mixing
 * the two — asserting a count for a screen nobody rendered — is the one thing this panel
 * must never do. That is also why the measured line appears under whichever section matches
 * the LIVE surface and never under the other one.
 *
 * Body copy is `body3`, matching the glossary description above each control rather than a
 * number picked for this panel. It was `body4` (12px) against that description's 14px, which
 * made the answer smaller than the question.
 */
export const TokenImpactPanel = ({
  token,
  active,
  surface,
  parts,
  invisible,
  previewLabel,
}: Props) => {
  const theme = useTheme()
  const impact = TOKEN_IMPACT[token]
  const panelId = panelIdFor(token)

  const liveInApp = surface === 'certificate'
  const measured = parts ?? []
  const total = measured.reduce((n, p) => n + p.count, 0)

  if (!active) return null

  const body = { lineHeight: 1.5 } as const

  /** The measured line, or the two different ways of saying "nothing outlined". */
  const measuredLine = (
    <Typography variant="body3" color="text.secondary" sx={body}>
      {total > 0 ? (
        <>
          {measured.map(p => `${p.label} (${p.count})`).join(' · ')}
          {previewLabel ? `, on ${previewLabel}.` : '.'}
        </>
      ) : invisible && invisible > 0 ? (
        /*
          NOT "nothing here" — the honest sentence is that you cannot SEE them, not that
          they do not exist. See the `invisible` prop's doc for the case this was written
          for (`border_radius`, since fixed by VRG-562) and why the branch outlives it.
        */
        `Nothing visible here. ${
          invisible === 1
            ? '1 element on this screen uses it, inside a panel or sheet that is closed.'
            : `${invisible} elements on this screen use it, all inside a panel or sheet that is closed.`
        }`
      ) : liveInApp ? (
        'Nothing on this screen. Try another flow above the preview.'
      ) : (
        'Nothing in this template. Try another above the preview.'
      )}
    </Typography>
  )

  return (
    /*
      THREE elements, and each one is doing a job the others cannot:

        outer  — the grid whose single row animates 0fr -> 1fr. Carries NO padding, border or
                 background, because anything with its own height would survive the collapse:
                 at 0fr you would get a 24px bordered strip snapping open, which is the same
                 flash in a smaller box.
        clip   — `overflow: hidden` + `min-height: 0`. The grid item. Both are required (see
                 the keyframes note); without them the row never shrinks and nothing animates.
        inner  — the visible panel. All the styling lives here, at full size throughout, and
                 is simply revealed.

      `mt` stays on the outer and so does not animate. It is 12px for 180ms; collapsing the
      gap too would mean animating a margin as well for a difference nobody can see.
    */
    <Box
      id={panelId}
      sx={{
        mt: 3,
        display: 'grid',
        gridTemplateRows: '1fr',
        // 260ms, not the 220 this started at. The house easing is a DECELERATE curve — fast
        // out, long settle — so on a short duration most of the travel lands in the first
        // ~60ms and the open still reads as a snap, which is the complaint being fixed. The
        // extra 40ms is spent on the part of the curve you actually perceive.
        animation: `${panelIn} 260ms cubic-bezier(0.32, 0.72, 0, 1)`,
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      }}
    >
      <Box sx={{ overflow: 'hidden', minHeight: 0 }}>
      <Stack
        spacing={3}
        sx={{
          p: 3,
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
          backgroundColor: alpha(theme.palette.primary.main, 0.03),
        }}
      >
        {/* ── On app preview: what is outlined, and what that is a sample of ───── */}
        <Stack spacing={1}>
          <Typography variant="body3" sx={{ fontWeight: 600 }}>
            On app preview
          </Typography>
          {liveInApp && measuredLine}
          <Typography variant="body3" color="text.secondary" sx={body}>
            {impact.appWide
              ? `Every screen your customers see, ${APP_SCREEN_COUNT} in all. This preview shows ${PREVIEWABLE_FLOW_COUNT}, so anything outlined is a sample.`
              : `${list(impact.previewableFlows.map(flowLabel))}. This preview shows ${
                  impact.previewableFlows.length === 1 ? 'that one' : 'all of them'
                }.`}
            {impact.offPreviewSurfaces.length > 0 &&
              ` Not shown here: ${list(impact.offPreviewSurfaces)}.`}
          </Typography>
        </Stack>

        {/* ── On email preview ──────────────────────────────────────────────────── */}
        <Stack spacing={1}>
          <Typography variant="body3" sx={{ fontWeight: 600 }}>
            On email preview
          </Typography>
          {!liveInApp && measuredLine}
          <Typography variant="body3" color="text.secondary" sx={body}>
            {impact.emailReach}
            {impact.emailCaveat ? ` ${impact.emailCaveat}` : ''}
          </Typography>
        </Stack>
      </Stack>
      </Box>
    </Box>
  )
}
